import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import StatusChip, { StatusChipTone } from '../common/StatusChip';
import type { InboundBadgeDescriptor } from '../../utils/inboundAdapters';

/**
 * `inbox/InboundQualityChip` (M-C, architecture-ux-density-v2.md §5.7/§9).
 *
 * Aggregates the §10 badge-descriptor list of an inbound document into ONE
 * `StatusChip`: a short, dominant-badge token in the table cell (instead of
 * the multi-pill `InboundBadgeList`) with a popover that still lists EVERY
 * badge, using the EXISTING whitelist-approved i18n keys/params verbatim
 * (`b.i18nKey`, `b.params`) — only the new short forms (`badge.short.*`) are
 * new text, and they deliberately carry no legal/compliance claim.
 *
 * `badges.length === 0` renders `null`, matching `InboundBadgeList`'s
 * existing behaviour (no empty chip).
 *
 * @module components/inbox/InboundQualityChip
 */
export interface InboundQualityChipProps {
  badges: InboundBadgeDescriptor[];
}

/**
 * Severity rank per badge id — LOWER wins the dominant-badge slot (§5.7):
 * danger(businessRuleError, formatError) > warning(duplicate, senderAuthFailed,
 * forwardingDamage) > caution(intraCommunity, mixedCategories, taxSchemeUnknown)
 * > info(deferred) > success(eInvoiceValid) > neutral(sonstigeRechnung, pending, other).
 *
 * ⚠️ Spec gap (documented, changelog-ux-density-inbox.md): `deriveInboundBadges`
 * (`utils/inboundAdapters.ts`) also emits a `reverseCharge13b` id (tone
 * `warning`) that architecture §5.7/§9's severity list does not mention. It is
 * grouped here with the other secondary `warning`-tone badges (duplicate/
 * senderAuthFailed/forwardingDamage) — matching its actual `InboundBadgeTone`
 * — rather than left unranked (which would silently fall to the lowest tier).
 */
const SEVERITY_RANK: Record<string, number> = {
  businessRuleError: 0,
  formatError: 0,
  duplicate: 1,
  senderAuthFailed: 1,
  forwardingDamage: 1,
  reverseCharge13b: 1,
  intraCommunity: 2,
  mixedCategories: 2,
  taxSchemeUnknown: 2,
  // „Manuell kontiert" (info) — rangiert wie andere Info-Badges (deferred),
  // NIE über eine offene „Bitte prüfen"-Auffälligkeit (§6.5).
  taxSchemeManual: 3,
  deferred: 3,
  eInvoiceValid: 4,
  sonstigeRechnung: 5,
  pending: 5,
  other: 5,
};

const DEFAULT_RANK = 5;

/**
 * §9 footnote: the two "hard" e-invoice error badges carry `InboundBadgeTone`
 * `'warning'` in `deriveInboundBadges` (the Bestand `InboundBadgeTone` union
 * has no `danger`), but the AGGREGATE chip is allowed to escalate them to
 * `danger` for visual dominance — the popover list still renders every badge
 * with its ORIGINAL tone/wording, untouched.
 */
const CHIP_DANGER_IDS = new Set(['businessRuleError', 'formatError']);

/** DE reference short forms (§9) — used as the `t()` fallback when a locale key is missing. */
const SHORT_FALLBACK_DE: Record<string, string> = {
  eInvoiceValid: 'E-Rechnung ✓',
  businessRuleError: 'Regelfehler',
  formatError: 'Formatfehler',
  duplicate: 'Dublette',
  senderAuthFailed: 'Absender ?',
  forwardingDamage: 'Beschädigt',
  intraCommunity: 'EU-Erwerb',
  mixedCategories: 'Bitte prüfen',
  taxSchemeUnknown: 'Bitte prüfen',
  taxSchemeManual: 'Kontiert',
  deferred: 'Später',
  sonstigeRechnung: 'Sonstige',
  pending: 'Ausstehend',
  other: 'Sonstig',
  reverseCharge13b: '§ 13b',
};

/** Picks the badge with the lowest severity rank (first occurrence wins ties). */
function pickDominant(badges: InboundBadgeDescriptor[]): InboundBadgeDescriptor {
  return badges.reduce((best, candidate) => {
    const candidateRank = SEVERITY_RANK[candidate.id] ?? DEFAULT_RANK;
    const bestRank = SEVERITY_RANK[best.id] ?? DEFAULT_RANK;
    return candidateRank < bestRank ? candidate : best;
  }, badges[0]);
}

export function InboundQualityChip({ badges }: InboundQualityChipProps) {
  const { t } = useTranslation('posteingang');

  const dominant = useMemo(() => (badges.length > 0 ? pickDominant(badges) : null), [badges]);

  if (!dominant) return null;

  const tone: StatusChipTone = CHIP_DANGER_IDS.has(dominant.id) ? 'danger' : dominant.tone;
  const extraCount = badges.length - 1;
  const shortLabel = t(`badge.short.${dominant.id}`, SHORT_FALLBACK_DE[dominant.id] ?? dominant.id);

  return (
    <StatusChip
      tone={tone}
      label={extraCount > 0 ? `${shortLabel} +${extraCount}` : shortLabel}
      detailTitle={t('unifiedInbox.table.quality', 'Prüfung')}
      detail={
        <ul className="space-y-1">
          {badges.map((badge) => (
            <li key={badge.id} data-badge-id={badge.id}>
              {t(badge.i18nKey, badge.params)}
            </li>
          ))}
        </ul>
      }
      data-testid="inbound-quality-chip"
    />
  );
}

export default InboundQualityChip;
